import React from 'react';
import {
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
  Box,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Landscape as LandscapeIcon,
  People as PeopleIcon,
  WbSunny as WeatherIcon,
  ArrowUpward as ArrowUpwardIcon,
  Opacity as OpacityIcon,
  LocalFireDepartment as FireIcon,
  Handshake as HandshakeIcon,
} from '@mui/icons-material';
// Get color based on FDI category
const getFdiColor = (category) => {
  switch (category) {
    case 'Safe':
      return '#2196f3'; // Blue
    case 'Moderate':
      return '#4caf50'; // Green
    case 'Dangerous':
      return '#d4b400'; // Darker Yellow for better readability
    case 'Very Dangerous':
      return '#ff9800'; // Orange
    case 'Extremely Dangerous':
      return '#f44336'; // Red
    default:
      return '#2196f3'; // Default to blue (Safe)
  }
};

const getWindDirectionRotation = (direction) => {
  // Convert cardinal direction to degrees if needed
  let degrees = 0;
  if (typeof direction === 'string') {
    const directions = {
      'N': 0, 'NNE': 22.5, 'NE': 45, 'ENE': 67.5,
      'E': 90, 'ESE': 112.5, 'SE': 135, 'SSE': 157.5,
      'S': 180, 'SSW': 202.5, 'SW': 225, 'WSW': 247.5,
      'W': 270, 'WNW': 292.5, 'NW': 315, 'NNW': 337.5
    };
    degrees = directions[direction] || 0;
  } else {
    degrees = direction;
  }

  // Add 180 degrees to make arrow point in the direction wind is blowing towards
  // instead of where it's coming from
  return (degrees + 180) % 360;
};
const WildfireDetailCards = ({ wildfire, weatherData, formatDate, calculateDuration }) => {
  return (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {/* Timeline Card */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Timeline
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Start Time
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDate(wildfire.start_time)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              End Time
            </Typography>
            <Typography variant="body1" gutterBottom>
              {formatDate(wildfire.end_time)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Duration
            </Typography>
            <Typography variant="body1">
              {calculateDuration(wildfire.start_time, wildfire.end_time)}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Area Card */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              <LandscapeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Area
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Size
            </Typography>
            <Typography variant="body1" gutterBottom>
              {wildfire.area_size_hectares?.toLocaleString() || 'N/A'} hectares
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Suburb
            </Typography>
            <Typography variant="body1" gutterBottom>
              {wildfire.suburb || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Address
            </Typography>
            <Typography variant="body1" gutterBottom>
              {wildfire.address || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Location
            </Typography>
            <Typography variant="body1">
              {wildfire.location ?
                `${wildfire.location.coordinates[1].toFixed(4)}, ${wildfire.location.coordinates[0].toFixed(4)}` :
                'N/A'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Impact Card */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Impact
            </Typography>
            <Typography variant="body2" color="text.secondary">
              On scene ({wildfire.people_involved?.toLocaleString() || '0'})
              {wildfire.mutual_aid_people > 0 &&
                ` — ${(
                  (wildfire.people_involved || 0) + wildfire.mutual_aid_people
                ).toLocaleString()} incl. assistance`}
            </Typography>
            {Array.isArray(wildfire.firefighters) && wildfire.firefighters.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5, mt: 0.5 }}>
                {wildfire.firefighters.map((f, idx) => {
                  const label = f.firefighter_id != null
                    ? `${f.name || ''} ${f.surname || ''}`.trim() || `#${f.firefighter_id}`
                    : (f.guest_name || 'Guest');
                  return (
                    <Chip
                      key={`ff-${idx}`}
                      size="small"
                      label={label}
                      color={f.firefighter_id != null ? 'primary' : 'default'}
                      variant={f.firefighter_id != null ? 'filled' : 'outlined'}
                    />
                  );
                })}
              </Box>
            ) : (
              <Typography variant="body1" gutterBottom>
                —
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Man Hours
            </Typography>
            <Typography variant="body1">
              {wildfire.man_hours?.toLocaleString() || '0'}
            </Typography>
            {/* Own crew above, whole fire below. GUFPA reports the combined
                figure; the stored field stays own-crew only. */}
            {wildfire.mutual_aid_man_hours > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                {((wildfire.man_hours || 0) + wildfire.mutual_aid_man_hours).toLocaleString()} incl. assistance
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              Injuries
            </Typography>
            <Typography variant="body1">
              {wildfire.injuries?.toLocaleString() || '0'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Mutual Aid Card — assistance RECEIVED. Rendered only when there is
          any, so a fire an FPA handled alone does not carry an empty card.
          These figures are deliberately separate from "On scene" above: that
          count feeds man-hours and the GUFPA export and covers this FPA own
          crew only. */}
      {Array.isArray(wildfire.mutual_aid) && wildfire.mutual_aid.length > 0 && (
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                <HandshakeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Mutual aid
              </Typography>
              {wildfire.mutual_aid.map((a, idx) => {
                const detail = [
                  a.people ? `${a.people} people` : null,
                  a.vehicles ? `${a.vehicles} vehicles` : null,
                  a.hours ? `${a.hours} h` : null,
                  // Derived server-side as people x hours; shown so the figure
                  // rolled into the GUFPA return is visible on the report it
                  // came from.
                  a.man_hours ? `${a.man_hours} man hrs` : null,
                ]
                  .filter(Boolean)
                  .join(' · ');
                return (
                  <Box key={`aid-${idx}`} sx={{ mb: 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {a.provider_name || a.provider_key || 'Unknown'}
                      {(() => {
                        // Tolerates the singular `station_name` on rows written
                        // before the picker went multi-select.
                        const names =
                          a.station_names && a.station_names.length
                            ? a.station_names
                            : a.station_name
                              ? [a.station_name]
                              : [];
                        return names.length ? ` — ${names.join(', ')}` : '';
                      })()}
                    </Typography>
                    {detail && (
                      <Typography variant="body2" color="text.secondary">
                        {detail}
                      </Typography>
                    )}
                    {a.vehicle_details && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {a.vehicle_details}
                      </Typography>
                    )}
                    {a.note && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {a.note}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Property Impact Card */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Property Impact
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Houses Damaged
            </Typography>
            <Typography variant="body1" gutterBottom>
              {wildfire.houses_damaged_count?.toLocaleString() || '0'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Houses Damage Value
            </Typography>
            <Typography variant="body1" gutterBottom>
              ${wildfire.houses_estimated_damage?.toLocaleString() || '0'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Livestock Lost
            </Typography>
            <Typography variant="body1">
              {wildfire.livestock_injured?.toLocaleString() || '0'}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Weather Card */}
      <Grid item xs={12} sm={6} md={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              <WeatherIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Weather
            </Typography>
            {weatherData && weatherData.length > 0 ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Temperature
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {weatherData[0].temperature}°C
                </Typography>

                {weatherData[0].precipitation !== undefined && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      <OpacityIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Precipitation
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {weatherData[0].precipitation} mm
                    </Typography>
                  </>
                )}

                <Typography variant="body2" color="text.secondary">
                  Wind/Gusts
                </Typography>
                <Typography variant="body1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  {weatherData[0].wind_speed} km/h
                  <ArrowUpwardIcon 
                    sx={{ 
                      mx: 1, 
                      fontSize: 18,
                      transform: `rotate(${ getWindDirectionRotation(weatherData[0].wind_direction)}deg)`,
                      color: weatherData[0].wind_speed > 30 ? 'error.main' : 'inherit'
                    }} 
                  />
                  {Math.round(weatherData[0].wind_speed * 1.5)} km/h
                </Typography>

                {weatherData[0].fire_danger_index !== undefined && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      <FireIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Fire Danger Index
                    </Typography>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: getFdiColor(weatherData[0].fire_danger_category)
                    }}>
                      <Typography variant="body1">
                        {weatherData[0].fire_danger_index}
                        {weatherData[0].fire_danger_category && ` (${weatherData[0].fire_danger_category})`}
                      </Typography>
                    </Box>
                  </>
                )}
              </>
            ) : (
              <Typography variant="body1">
                No weather data available
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default WildfireDetailCards;
